import { dataSourceFor, devQuickBatchConfig } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import { useState } from 'react'

import { useScreenAccess } from '../screenAccess.ts'

import { quickTag } from './devQuickBatch.ts'
import { runQuickReset } from './devQuickReset.ts'
import { useQuickResetPorts } from './quickResetPorts.ts'
import { useQuickSupply } from './useQuickSupply.ts'

/**
 * **시뮬 준비** — 시뮬레이션 개발계에서만, 작업 줄 [시작] · 작업 추가의 「사전 점검」 앞에 「테스트 작업 시작」과 같은 자동 준비를 돌린다
 * (eric 2026-10-14 「지금 시뮬레이션 상황에서만.. 이거 통과하게 해?」 → 「응 해줘」).
 *
 * 하는 일 — `runQuickReset`(안전 해제 · 이송 격리 해제 · 쥔 트레이 내려놓기 · 트레이 자리 정리 · 모듈 초기화 …) → `useQuickSupply`
 * (팁 · TC · 시약 보충 · 폐기통 교체 기록). 🔴 **시작하려는 작업은 남긴다**(`keepBatchId`) — 종착시키는 것은 그 밖의 진행 중 작업이다.
 * 🔴 6축이 쥔 트레이를 그 작업 자신이 막아 못 내려놓으면(`deferred: 'heldTray'`) 실패로 멈추지 않고 보충 뒤 시작 창으로 넘긴다
 *    (히나 실측 2026-10-23 ②⑤ — 종전엔 `MODULE_BUSY` 한 줄로 작업 줄에 멈췄다).
 *    🔴 서버 자리 정리(`POST /modules/prepare`)는 이때 보내지 않는다 — 그 연산은 비종착 작업을 전부 중단시켜 시작할 작업까지 끝냈다
 *    (2026-10-22 eric 「그냥 시작 버튼만 눌렀어」 · `devQuickReset.ts` 9단계).
 *
 * 🔴 **켜지는 조건은 「테스트 작업 시작」과 같다** — 개발 플래그(`devQuickBatchConfig().enabled` · `VITE_DEV_QUICK_BATCH`)가 켜졌고
 *    작업 · 시료가 실물(`live`)일 때. 고객 화면 · 실장비에는 그 플래그가 없어 이 흐름이 아예 돌지 않는다 — 사전 점검의 차단이 그대로 선다.
 * 🔴 사전 점검을 건너뛰는 것이 아니다 — 준비 뒤 서버 점검이 그대로 돈다. 판정 불능 항목 통과는 서버 설정
 *    (`PREFLIGHT_SKIP_UNEVALUABLE` · `PREFLIGHT_SKIP_DEVICE_READY`)의 몫이다.
 */
export interface SimStartPrepare {
  /** 이 환경에서 시뮬 준비가 도는가 */
  enabled: boolean
  /** 준비가 도는 중 */
  preparing: boolean
  /** 준비를 돌린다 — 성공이면 `undefined`, 멈췄으면 그 사유 한 줄 */
  prepare: () => Promise<string | undefined>
}

export function useSimStartPrepare({
  batchId,
  recipeId,
}: {
  batchId: string | undefined
  recipeId: string | undefined
}): SimStartPrepare {
  const access = useScreenAccess()
  const live =
    devQuickBatchConfig().enabled &&
    dataSourceFor('batches') === 'live' &&
    dataSourceFor('samples') === 'live'
  const ports = useQuickResetPorts(access)
  /*
   * 🔴 **누른 순간의 레시피를 붙잡는다**(2026-10-22 실측) — 준비 도중 그 작업이 활성 목록에서 빠지면 `recipeId` 가 `undefined` 가 되고,
   *    보충의 `refetch()` 는 `enabled` 를 무시해 `GET /recipes/undefined`(404)를 불렀다. 준비가 끝날 때까지 누른 때의 id 로 묻는다.
   */
  const [pinnedRecipeId, setPinnedRecipeId] = useState<string | undefined>(undefined)
  const supply = useQuickSupply({ live, recipeId: pinnedRecipeId ?? recipeId })
  const [preparing, setPreparing] = useState(false)

  const prepare = async (): Promise<string | undefined> => {
    if (!live || batchId === undefined) return undefined
    /* 레시피를 모르면 보충량을 못 정한다 — 레시피 조회(`/recipes/undefined`)도 자리 정리도 보내지 않고 멈춘다 */
    if (recipeId === undefined || recipeId === '')
      return t('dev.simPrepare.failed', {
        step: t('dev.simPrepare.supply'),
        reason: t('dev.simPrepare.noRecipe'),
      })
    setPinnedRecipeId(recipeId)
    setPreparing(true)
    try {
      const reset = await runQuickReset(ports, () => undefined, { keepBatchId: batchId })
      /*
       * 🔴 6축이 쥔 트레이를 이 작업 자신이 막아 못 내려놓았다 — 끊지 않는다. 보충은 마저 하고 시작 창(사전 점검)으로 넘긴다:
       *    그 줄의 [작업 중단 후 내려놓기]를 사람이 누를 때만 작업이 끊긴다(eric 2026-10-24 「작업은 유지하고」 · `HeldTrayFix.tsx`).
       */
      if (!reset.ok && reset.deferred !== 'heldTray')
        return t('dev.simPrepare.failed', {
          step: t(`dev.quickReset.step.${reset.step}`),
          reason: reset.reason,
        })
      const supplied = await supply(quickTag())
      if (supplied.refused !== undefined)
        return t('dev.simPrepare.failed', {
          step: t('dev.simPrepare.supply'),
          reason: supplied.refused,
        })
      return undefined
    } finally {
      setPreparing(false)
      setPinnedRecipeId(undefined)
    }
  }

  return { enabled: live, preparing, prepare }
}
